import { Check, Lock, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Empty, Media } from '../components/common';
import { useLab } from '../lib/service';
import {} from '../shared';
import { useSeries } from './context';

/** Character identity references and the one-way cast lock. */
export function CastTab() {
  const { detail: d, project: p, controlsDisabled, revision, run, act } = useSeries();
  const lab = useLab();
  return (
    <>
      <div className="section-heading">
        <div>
          <h2>Meet the people in your story.</h2>
          <p className="muted">Fictional performers. Consistent identities. Your direction.</p>
        </div>
        <Button
          disabled={controlsDisabled || p.castLocked || !d.characters.length}
          onClick={() => void act(() => lab.lockCast(p._id))}
        >
          <Lock size={15} />
          {p.castLocked ? 'Cast locked' : 'Lock cast'}
        </Button>
      </div>
      <div className="cast-grid">
        {d.characters.map((c, i) => {
          const casting = d.castings.filter((x) => x.characterId === c._id).at(-1),
            actor = d.actors.find((a) => a._id === casting?.actorId);
          return (
            <div className="cast-card" key={c._id}>
              <div className={`actor-portrait portrait-${i % 3}`}>
                {actor?.identityAssetId && !lab.isDemo ? (
                  <Media assetId={actor.identityAssetId} />
                ) : (
                  <>
                    <div className="portrait-silhouette">
                      <div />
                      <span />
                    </div>
                    <span className="portrait-initial">{c.data.name[0]}</span>
                    <small>{actor ? 'SIMULATED REFERENCE' : 'AWAITING CASTING'}</small>
                  </>
                )}
                {actor && (
                  <span className="actor-badge">
                    <Check size={12} />
                    {p.castLocked ? 'Identity locked' : 'Candidate selected'}
                  </span>
                )}
              </div>
              <div className="cast-card-body">
                <div className="cast-name">
                  <h3>{c.data.name}</h3>
                  <span>{c.data.age}</span>
                </div>
                <p className="muted">{c.data.role}</p>
                <p>{c.data.personality}</p>
                <div className="cast-voice">
                  <span>VOICE DIRECTION</span>
                  <p>{c.data.voice}</p>
                </div>
                <Button
                  variant="outline"
                  className="full-width"
                  disabled={controlsDisabled || p.castLocked}
                  onClick={() =>
                    void run('CAST_ACTOR', { characterId: c._id, instruction: revision })
                  }
                >
                  {actor ? <RefreshCw size={14} /> : <Sparkles size={14} />}{' '}
                  {actor ? 'New candidate' : 'Cast this character'}
                </Button>
                <select
                  className="actor-select"
                  aria-label={`Choose existing actor for ${c.data.name}`}
                  value={actor?._id ?? ''}
                  disabled={controlsDisabled || p.castLocked}
                  onChange={(e) => void act(() => lab.castExisting(p._id, c._id, e.target.value))}
                >
                  <option value="" disabled>
                    Or select an existing actor
                  </option>
                  {d.actors
                    .filter((a) => a.identityAssetId)
                    .map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.name}
                      </option>
                    ))}
                </select>
                {actor && actor.kind === 'custom' && lab.home?.access.role === 'owner' && (
                  <Button
                    variant="ghost"
                    className="full-width"
                    onClick={() => void act(() => lab.promoteActor(actor._id))}
                  >
                    Add to shared actor library
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!d.characters.length && (
        <Empty
          title="Your cast is waiting to be imagined"
          text="Develop the series pitch to discover its main characters."
        />
      )}
    </>
  );
}
